/**
 * stage5_fx — the shader ADDITIONS wave (2026-09-15) wired into Atlas: the
 * pure rules in one place so the harness pins them without a renderer, plus
 * the runtime registries the per-frame drivers write.
 *
 *   specular AA        · the variance cap per tier
 *   procedural filter  · the detail node's fade band
 *   light volume       · the fog volume's extinction from the weather + tier steps
 *   six-way smoke      · which light lights the smoke (sun by day, sky by night)
 *   area lights        · window emitter intensity by the night and the lit ratio
 *   glass              · which material becomes which glass tier
 *   anisotropy         · which metals brush, and how
 *   retroreflection    · which names carry the light-return lobe
 *   exposure metering  · average below med, histogram from med
 */
import { VEHICLE_SPEED_SCALE } from './hover_physics.js';

export const STAGE5_FLAGS = [
  'specularAA', 'proceduralFiltering', 'volumetricLights', 'litSmoke', 'areaLights',
  'glassTransmission', 'hardSurfaceAnisotropy', 'retroreflection',
] as const;
export type Stage5Flag = (typeof STAGE5_FLAGS)[number];

// ── window area lights × interiors (render wave 1, 2026-09-17) ──
/**
 * Whether a glass pane may seat one of the pooled rect lights. Outdoors every
 * pane in range may (the windows light the street); INSIDE a building only
 * the active interior's own panes may — an unshadowed rectangle lights
 * through walls, and the neighbour's lit window used to light this room.
 */
export function paneEligible(paneInteriorId: string | null, activeInteriorId: string | null): boolean {
  if (activeInteriorId === null) return true;
  return paneInteriorId === activeInteriorId;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (v: number): number => { const t = clamp01(v); return t * t * (3 - 2 * t); };
const hash01 = (s: string): number => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return ((h >>> 0) % 100000) / 100000; };

// ── specular AA ──
/** How much variance a shortened normal-map texel may add (α² domain): cheap tiers cap harder. */
export function specularAAVarianceCap(tier: string): number {
  return tier === 'low' ? 0.12 : tier === 'med' ? 0.2 : 0.3;
}

// ── procedural filtering ──
/** The detail node's fade band in noise periods per pixel (start, end). */
export function detailFilterBand(tier: string): [number, number] {
  return tier === 'low' ? [0.25, 0.9] : [0.35, 1.2];
}

// ── the shadowed light volume ──
export const VOLUME_SIZE_M: [number, number, number] = [72, 26, 72];
/** March steps per pixel by tier. */
export function volumeStepsFor(tier: string): number {
  return tier === 'ultra' ? 32 : tier === 'high' ? 24 : 16;
}
/**
 * Extinction per metre for the volume from the atmosphere's fog density (a
 * FogExp2 density, ~1/metres) and the weather: a clear day is a faint haze the
 * sun still draws shafts through; fog, rain and dust thicken it; night halves
 * the ambient floor so the volume does not glow.
 */
export function volumeDensityFor(fogDensity: number, weatherKind: string, dust: number): number {
  const base = Math.max(0.003, Math.min(0.06, fogDensity * 2.5)); // a clear day: ~250 m of faint haze the sun still draws shafts through
  const wx = weatherKind === 'fog' ? 2.2 : weatherKind === 'rain' || weatherKind === 'storm' ? 1.4 : weatherKind === 'snow' ? 1.6 : 1;
  return Math.min(0.12, base * wx + clamp01(dust) * 0.05);
}
/** Ambient in-scatter scale by the night factor (0 day … 1 night). */
export function volumeAmbientScale(night: number): number { return 1 - 0.7 * smooth(clamp01(night)); }

// ── six-way smoke ──
/**
 * The light that lights the smoke: the sun while it is up (its direction and
 * an illuminance falling with elevation), a faint zenith sky at night. Returns
 * the direction TOWARD the light and a colour scale.
 */
export function sixWayLightFor(sunDir: [number, number, number], night: number): { dir: [number, number, number]; color: [number, number, number]; ambient: [number, number, number] } {
  const n = smooth(clamp01(night));
  const el = Math.max(0, sunDir[1]);
  const sun = (1 - n) * Math.min(1, el * 2.5);
  const dir: [number, number, number] = sun > 0.02 ? sunDir : [0, 1, 0];
  const warm = [1.0, 0.93, 0.82] as const;
  return {
    dir,
    color: [warm[0] * sun + 0.06, warm[1] * sun + 0.07, warm[2] * sun + 0.09],
    ambient: [0.18 * (1 - 0.6 * n) + 0.02, 0.2 * (1 - 0.6 * n) + 0.02, 0.24 * (1 - 0.6 * n) + 0.03],
  };
}

// ── area lights ──
export const AREA_LIGHT_POOL = 6;
export const AREA_LIGHT_RANGE_M = 90;
/** Emitter radiance for a lit window: nothing by day, up with the lit ratio at night. */
export function areaLightIntensity(night: number, litRatio: number, max = 6): number {
  return max * smooth((clamp01(night) - 0.25) / 0.4) * clamp01(litRatio);
}

// ── glass ──
export type GlassTierPick = 'thin' | 'thick' | null;
/**
 * Which glass tier a material takes. Structures keep interior mapping (their
 * panes are the rooms); props/decor/vehicle glass becomes thin transmission,
 * and anything named as a canopy, visor, dome or vitrine gets the thick tier.
 */
export function glassTierFor(kind: string, name: string | undefined | null): GlassTierPick {
  if (!name || name.includes('#')) return null;
  if (!/glass|window|pane|canopy|visor|dome|vitrine|lens/i.test(name)) return null;
  if (kind === 'structure') return null;
  return /canopy|visor|dome|vitrine|lens/i.test(name) ? 'thick' : 'thin';
}

// ── anisotropy ──
/** Brushed metal: strength by metalness and the part's name, direction from the name hash (consistent per part). */
export function anisotropyFor(metalness: number, name: string | undefined | null): { strength: number; rotation: number } | null {
  if (metalness < 0.6) return null;
  const n = name ?? '';
  const strength = /barrel|gun|rifle|blade|rail|pipe|tube|axle|rotor/i.test(n) ? 0.6 : 0.35;
  return { strength, rotation: hash01(n) * Math.PI };
}

// ── retroreflection ──
/** The light-return lobe only on things that really carry a reflective film. */
export function retroFor(name: string | undefined | null): number {
  if (!name) return 0;
  return /sign|marker|reflect|beacon|bollard|cats?eye|lane|stripe|safety/i.test(name) ? 0.8 : 0;
}

// ── foliage transmission (audit 16.2) ──
/** Light through the leaves: follows the sun's elevation, nothing at night. */
export function foliageTransmissionIntensity(sunY: number, night: number): number {
  return (1 - smooth(clamp01(night))) * clamp01((sunY + 0.02) * 3);
}

// ── exposure ──
export function exposureMeteringFor(tier: string): 'average' | 'histogram' {
  return tier === 'minimal' || tier === 'potato' || tier === 'low' ? 'average' : 'histogram';
}

/** The headlight for the retro lobe at night: on a ride, ahead of the hull, scaled by speed. */
export function headlightFor(riding: boolean, night: number, speed: number): number {
  if (!riding) return 0;
  return smooth((clamp01(night) - 0.2) / 0.4) * Math.min(1, 0.4 + speed / (40 * VEHICLE_SPEED_SCALE));
}

// ── Runtime registries ────────────────────────────────────────────────────
export interface SixWayHandleLike { lightDirWorld: { value: { set(x: number, y: number, z: number): unknown } }; lightColor: { value: { set(x: number, y: number, z: number): unknown } }; ambientColor: { value: { set(x: number, y: number, z: number): unknown } } }
export const sixWayHandles = new Set<SixWayHandleLike>();
export interface RetroHandleLike { lightDirView: { value: { set(x: number, y: number, z: number): unknown } }; lightColor: { value: { set(x: number, y: number, z: number): unknown } } }
export const retroHandles = new Set<RetroHandleLike>();
export interface SpecularAAHandleLike { varianceCap: { value: number } }
export const specularAAHandles = new Set<SpecularAAHandleLike>();

export const stage5Stats = {
  specularAAMats: 0,
  filteredDetailMats: 0,
  volumeBuilt: false,
  volumeDensity: 0,
  volumeSteps: 0,
  sixWayEmitters: 0,
  sixWayPuffs: 0,
  areaLightsSeated: 0,
  areaLightIntensity: 0,
  glassThin: 0,
  glassThick: 0,
  anisotropicMats: 0,
  retroMats: 0,
  exposureMetering: 'average' as 'average' | 'histogram',
};
